import { StockWithVariant } from '@/api/daily-stock-api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { Calendar, Minus, MoreHorizontal, Plus } from 'lucide-react';
import { StockMovementInfo } from './StockMovementInfoTooltip';
import { StockStats, StockTableRowProps } from './type';

export function StockTableRow({ stock, onAdjustOut, onAdjustIn, index = 0 }: StockTableRowProps) {
  const getStockStats = (stock: StockWithVariant): StockStats => {
    const totalStock = stock.stockInHand ?? 0;
    const totalSold = stock.soldQty;
    const totalReceived = stock.receiveQty || 0;
    const netMovement = totalReceived - totalSold + stock.adjustInQty - stock.adjustOutQty;

    let stockStatus: 'In Stock' | 'Low Stock' | 'Out of Stock' = 'In Stock';
    if (totalStock === 0) {
      stockStatus = 'Out of Stock';
    } else if (totalStock < 10) {
      stockStatus = 'Low Stock';
    }

    return {
      totalStock,
      totalSold,
      totalReceived,
      netMovement,
      stockStatus,
    };
  };

  const stats = getStockStats(stock);
  const variantName = stock.variantName;
  const variantImageUrl = stock.imageUrl || null;
  const variantSku = stock.sku;

  const isEven = index % 2 === 0;

  return (
    <TableRow
      className={`
        transition-all duration-150
        border-b border-slate-200/60
        border-l-2 border-l-transparent
        ${isEven ? 'bg-white' : 'bg-slate-50/40'}
        hover:bg-blue-50/60 hover:shadow-sm
        hover:border-blue-200/60 hover:border-l-blue-500
        group
      `}
    >
      {/* Variant Column */}
      <TableCell className="py-3 px-2 md:px-4 min-w-0">
        <div className="flex items-center space-x-2 min-w-0">
          <Avatar className="h-8 w-8 md:h-10 md:w-10 ring-1 ring-slate-100 shrink-0">
            <AvatarImage src={variantImageUrl || undefined} />
            <AvatarFallback className="bg-gradient-to-br from-blue-50 to-indigo-100 text-slate-600 font-semibold text-xs">
              {variantName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 text-xs md:text-sm truncate">
              {variantName}
            </div>
            <div className="text-xs font-mono text-slate-600 truncate mt-0.5">
              SKU: {variantSku || 'N/A'}
            </div>
          </div>
        </div>
      </TableCell>

      {/* Stock Level Column */}
      {/* <TableCell className="py-2 px-2 text-center">
        <StockLevelInfo stock={stock} stats={stats} />
      </TableCell> */}

      {/* Movement Column */}
      <TableCell className="py-3 px-2 md:px-3 text-center min-w-0">
        <StockMovementInfo stock={stock} stats={stats} />
      </TableCell>

      {/* Price Column */}
      <TableCell className="py-3 px-2 md:px-3 text-right min-w-0">
        <PriceInfo price={stock.price} />
      </TableCell>

      {/* Date Column */}
      <TableCell className="py-3 px-2 md:px-3 text-center min-w-0">
        <DateInfo stockDate={stock.stockDate} />
      </TableCell>

      {/* Action Column */}
      <TableCell className="py-3 px-2 md:px-3 shrink-0">
        <ActionMenu stock={stock} onAdjustOut={onAdjustOut} onAdjustIn={onAdjustIn} />
      </TableCell>
    </TableRow>
  );
}

// Price Information Component
function PriceInfo({ price }: { price: number | null }) {
  return (
    <div className="flex items-center justify-end space-x-1.5">
      <span className="text-sm font-bold text-green-700">
        {price ? `$${price.toFixed(2)}` : 'N/A'}
      </span>
    </div>
  );
}

// Status Information Component

// Date Information Component
function DateInfo({ stockDate }: { stockDate: string }) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex flex-col items-center space-y-0.5">
      <div className="flex items-center space-x-1.5">
        <Calendar className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-xs font-medium text-slate-700">{formatDate(stockDate)}</span>
      </div>
    </div>
  );
}

// Action Menu Component
function ActionMenu({
  stock,
  onAdjustOut,
  onAdjustIn,
}: {
  stock: StockWithVariant;
  onAdjustOut?: (stock: StockWithVariant) => void;
  onAdjustIn?: (stock: StockWithVariant) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => onAdjustIn?.(stock)}
          className="text-green-600 focus:text-green-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Adjust In
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onAdjustOut?.(stock)}
          className="text-red-600 focus:text-red-600"
        >
          <Minus className="mr-2 h-4 w-4" />
          Adjust Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
